import { useState } from "react";
import { X, Mail, MessageSquare, Link2, Copy, Check, Send, User } from "lucide-react";

interface ShareDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShare?: (data: { method: string; recipient?: string; message?: string }) => void;
  document?: {
    name: string;
    category: string;
  };
  clientName?: string;
  clientEmail?: string;
}

const shareOptions = [
  { id: "email", label: "Email", icon: Mail, description: "Send directly to client's email" },
  { id: "sms", label: "SMS", icon: MessageSquare, description: "Send via text message" },
  { id: "link", label: "Copy Link", icon: Link2, description: "Generate a shareable link" },
];

export default function ShareDocumentModal({
  isOpen,
  onClose,
  onShare,
  document,
  clientName = "Sarah Miller",
  clientEmail = "sarah.miller@email.com",
}: ShareDocumentModalProps) {
  const [selectedMethod, setSelectedMethod] = useState("email");
  const [customEmail, setCustomEmail] = useState("");
  const [useClientEmail, setUseClientEmail] = useState(true);
  const [message, setMessage] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);

  const handleCopyLink = () => {
    const link = `https://glossa.com/documents/shared/${document?.name?.replace(/\s+/g, '-').toLowerCase()}`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleShare = async () => {
    setIsSending(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    onShare?.({
      method: selectedMethod,
      recipient: useClientEmail ? clientEmail : customEmail,
      message,
    });
    
    setIsSending(false);
    setShareSuccess(true);
    
    setTimeout(() => {
      setShareSuccess(false);
      resetForm();
      onClose();
    }, 1500);
  };

  const resetForm = () => {
    setSelectedMethod("email");
    setCustomEmail("");
    setUseClientEmail(true);
    setMessage("");
    setLinkCopied(false);
  };

  const recipientEmail = useClientEmail ? clientEmail : customEmail;
  const isValid = selectedMethod === "link" || (recipientEmail && recipientEmail.includes("@"));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-[#e8e0f0]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#f0ebf5]">
          <div>
            <h2 className="text-lg font-bold text-[#2d2d2d]">Share Document</h2>
            {document && (
              <p className="text-sm text-[#6b6b6b]">{document.name}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-[#f8f5fc] rounded-lg transition-colors text-[#6b6b6b] hover:text-[#3d3d3d]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-5">
          {/* Share Method Selection */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-3">
              Share via
            </label>
            <div className="grid grid-cols-3 gap-2">
              {shareOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.id}
                    onClick={() => setSelectedMethod(option.id)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors ${
                      selectedMethod === option.id
                        ? "border-[#704aaf] bg-[#f8f5fc]"
                        : "border-[#e8e0f0] hover:bg-[#faf8fc]"
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${selectedMethod === option.id ? "text-[#704aaf]" : "text-[#6b6b6b]"}`} />
                    <span className={`text-xs font-medium ${selectedMethod === option.id ? "text-[#704aaf]" : "text-[#6b6b6b]"}`}>
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Email/SMS Content */}
          {(selectedMethod === "email" || selectedMethod === "sms") && (
            <>
              {/* Recipient */}
              <div>
                <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
                  Recipient
                </label>
                
                {/* Use Client Email Option */}
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="use-client-email"
                    checked={useClientEmail}
                    onChange={(e) => setUseClientEmail(e.target.checked)}
                    className="w-4 h-4 rounded border-[#e8e0f0] text-[#704aaf] focus:ring-[#704aaf]"
                  />
                  <label htmlFor="use-client-email" className="text-sm text-[#5c5c5c]">
                    Send to client ({clientEmail})
                  </label>
                </div>

                {!useClientEmail && (
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9b9b9b]" />
                    <input
                      type={selectedMethod === "email" ? "email" : "tel"}
                      value={customEmail}
                      onChange={(e) => setCustomEmail(e.target.value)}
                      placeholder={selectedMethod === "email" ? "Enter email address" : "Enter phone number"}
                      className="w-full pl-10 pr-4 py-2.5 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] placeholder-[#9b9b9b] focus:outline-none focus:ring-2 focus:ring-[#e8e0f0] focus:border-[#704aaf] transition-all"
                    />
                  </div>
                )}
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
                  Message (Optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a personal message..."
                  className="w-full px-3 py-2.5 border border-[#e8e0f0] rounded-lg text-sm text-[#3d3d3d] placeholder-[#9b9b9b] resize-none h-20 focus:outline-none focus:ring-2 focus:ring-[#e8e0f0] focus:border-[#704aaf] transition-all"
                />
              </div>
            </>
          )}

          {/* Link Content */}
          {selectedMethod === "link" && (
            <div>
              <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
                Shareable Link
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2.5 bg-[#f8f5fc] border border-[#e8e0f0] rounded-lg text-sm text-[#6b6b6b] truncate">
                  https://glossa.com/documents/shared/{document?.name?.replace(/\s+/g, '-').toLowerCase()}
                </div>
                <button
                  onClick={handleCopyLink}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    linkCopied
                      ? "bg-[#e6f7ed] text-[#0d7a3e]"
                      : "bg-[#704aaf] text-white hover:bg-[#764BB7]"
                  }`}
                >
                  {linkCopied ? (
                    <span className="flex items-center gap-1.5">
                      <Check className="w-4 h-4" />
                      Copied
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      <Copy className="w-4 h-4" />
                      Copy
                    </span>
                  )}
                </button>
              </div>
              <p className="text-xs text-[#9b9b9b] mt-2">
                Anyone with this link can view the document
              </p>
            </div>
          )}

          {/* Success Message */}
          {shareSuccess && (
            <div className="p-3 bg-[#e6f7ed] border border-[#b8e6cd] rounded-lg">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#0d7a3e]" />
                <span className="text-sm text-[#0d7a3e]">
                  Document shared successfully!
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[#f0ebf5] bg-[#faf8fc]">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[#6b6b6b] hover:bg-[#f0ebf5] rounded-lg transition-colors"
            >
              Cancel
            </button>
            {selectedMethod !== "link" && (
              <button
                onClick={handleShare}
                disabled={!isValid || isSending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#764BB7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Share Document
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


